import { weenatIntl } from '@weenat/wintl';
import isNil from 'lodash-es/isNil';
import { useMemo } from 'react';
import { useClient, useQuery } from '../index';
import { isPlotSummary } from '../resources/measurements.type';
import { declineColorForHorizon } from './colors';
/**
 * Fetches all horizon and formats them as select options
 * @param useRawLabel whether or not we should format the horizons label (add the unit)
 * @param idsToKeep the ids to keep
 * @returns all horizons as options or only the given horizon inside @param idsToKeep
 */
export const useAllHorizonsOptions = (useRawLabel, idsToKeep, enabled = true) => {
    const client = useClient();
    const horizons = useQuery(client.horizons.getAllPage(), { enabled }).data || EMPTY_ARRAY;
    return useMemo(() => {
        return horizons
            .sort((horizonA, horizonB) => (horizonA.value > horizonB.value ? 1 : -1))
            .reduce((acc, hrz) => {
            if (isNil(idsToKeep) || (!isNil(idsToKeep) && idsToKeep.includes(hrz.id)))
                acc.push({
                    value: hrz.id,
                    label: useRawLabel
                        ? hrz.value.toString()
                        : `${hrz.value}${weenatIntl.t('units.centimeters_abbr')}`
                });
            return acc;
        }, []);
    }, [horizons, useRawLabel, idsToKeep]);
};
/**
 * @param horizonId to format as an option
 * @param useRawLabel whether or not we should format the horizon label (add the unit)
 * @returns a specific horizon option matching the given @param horizonId
 */
export const useHorizonOption = (horizonId, useRawLabel) => {
    const allOptions = useAllHorizonsOptions(useRawLabel);
    return !isNil(horizonId) ? allOptions.find(({ value }) => value === horizonId) : undefined;
};
const isHPOTKey = (key) => key.includes('HPOT_');
/**
 * Parsing a plot summary to extract the available horizons
 * @param plotSummary to analyze
 * @returns a Set of available horizons ids
 */
export function extractAvailableHorizonIdsSet(plotSummary) {
    return !isNil(plotSummary) && !isNil(plotSummary.data)
        ? Object.keys(plotSummary.data).reduce((acc, key) => {
            // If we have some matching keys, for each of them we extract the keys of the object (horizon ids)
            // And add them to the Set
            if (isHPOTKey(key)) {
                const valuePerDepth = plotSummary.data[key];
                if (!isNil(valuePerDepth))
                    Object.keys(valuePerDepth).forEach((id) => acc.add(parseInt(id, 10)));
            }
            return acc;
        }, new Set())
        : new Set();
}
/**
 * Extracts all available horizon from all summaries into a single array
 * @param summaries to analyze
 * @returns an array of ids
 */
function extractAllHorizonIds(summaries) {
    const allHorizonIds = new Set();
    summaries.forEach((ps) => {
        const horizonIdsSet = isPlotSummary(ps) ? extractAvailableHorizonIdsSet(ps) : new Set();
        horizonIdsSet.forEach((id) => allHorizonIds.add(id));
    });
    return Array.from(allHorizonIds.values());
}
const EMPTY_ARRAY = [];
export const useExtractAvailableHorizonsFromSummaries = (summaries, enabled = true) => {
    const horizonIds = useMemo(() => extractAllHorizonIds(summaries), [summaries]);
    const options = useAllHorizonsOptions(true, horizonIds, enabled);
    return options;
};
export const addColorToHorizons = (metric, horizons) => {
    return horizons.map((h) => {
        h.color = declineColorForHorizon(metric, h);
        return h;
    });
};
